import { AloftArea, AloftDate, AloftPrice } from '@aloft/shared-objects';
import {
  LatitudeLongitude,
  ManualAdjustments,
  addressToString,
  expected,
  getAbbreviatedCarportGarage,
  isDefined,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../redux/types.ts';
import { SalesComparisonAdjustmentKey, SalesComparisonGridColumnData } from '../types.ts';
import {
  formatAdjustmentPrice,
  formatFeatureArea,
  formatFeatureAreas,
  getBathCountAdjustment,
  getDistanceToSubject,
  getFoundationDisplay,
  getPropertyImageWithFallback,
} from './shared.tsx';

export const convertSalesComparisonDataToPDFGridColumn = async (
  id: 'subject' | 'subjectArv' | string,
  compSequence: string[],
  data?: CompPropertyDataState['compData'] | SubjectPropertyDataState['subjectData'],
  adjustments?: CompAdjustments,
  manualAdjustments?: ManualAdjustments & { id?: string },
  subjectCoords?: LatitudeLongitude,
  entityListingPhotos?: Blob[],
  effectiveYear?: number,
): Promise<SalesComparisonGridColumnData | null> => {
  if (!data) return null;

  const {
    editableData: { propertyIdentification, transaction, site },
    readonlyData: { backupListingData },
  } = data;

  let { improvements } = data.editableData;

  if (id === 'subjectArv') {
    improvements = (data as SubjectPropertyDataState['subjectData']).editableData.arv!.improvements;
  }

  const address = orUndefined(addressToString)(propertyIdentification?.address) ?? '';

  let propertyImage: Blob | string | undefined = entityListingPhotos?.[0];

  if (!propertyImage) {
    propertyImage = await getPropertyImageWithFallback(
      backupListingData?.listing.Media?.[0]?.MediaURL,
      propertyIdentification?.coordinates,
    );
  }

  const distanceToSubject = getDistanceToSubject(
    subjectCoords,
    propertyIdentification?.coordinates,
  );

  let arv: SalesComparisonGridColumnData['arv'] | undefined;

  if (isDefined((data as SubjectPropertyDataState['subjectData']).editableData.arv)) {
    const arvImprovements = expected(
      (data as SubjectPropertyDataState['subjectData']).editableData.arv,
    ).improvements;

    arv = {
      gla: arvImprovements.gla
        ? AloftArea.fromNumber(arvImprovements.gla.unitAmount, arvImprovements.gla.unit).toString(
            true,
          )
        : '',
      bedCount: arvImprovements.bedrooms ? arvImprovements.bedrooms.toString() : '',
      bathCount:
        arvImprovements.halfBaths || arvImprovements.fullBaths
          ? `${arvImprovements.fullBaths || 0} / ${arvImprovements.halfBaths || 0}`
          : '',
      basement: formatFeatureAreas([
        {
          area: arvImprovements.basement?.totalArea?.unitAmount,
          unit: arvImprovements.basement?.totalArea?.unit,
        },
        {
          area: arvImprovements.basement?.finishedArea?.unitAmount,
          unit: arvImprovements.basement?.finishedArea?.unit,
        },
      ]),
      quality: `Q${arvImprovements.uadQuality ?? '-'}/${arvImprovements.craftsmanQuality ?? '-'}${getFoundationDisplay(arvImprovements)}`,
      condition: `C${arvImprovements.uadCondition ?? '-'}/E${arvImprovements.effectiveAge ?? '-'}`,
    };
  }

  const yearBuilt =
    effectiveYear && improvements.age ? (effectiveYear - improvements.age).toString() : undefined;

  const aboveGradeHalfBaths = improvements.halfBaths || 0;
  const belowGradeHalfBaths =
    improvements.basement?.hasFeature && improvements.basement.halfBaths
      ? improvements.basement.halfBaths
      : 0;
  const totalHalfBaths = (aboveGradeHalfBaths + belowGradeHalfBaths).toString();

  const aboveGradeFullBaths = improvements.fullBaths || 0;
  const belowGradeFullBaths =
    improvements.basement?.hasFeature && improvements.basement.fullBaths
      ? improvements.basement.fullBaths
      : 0;
  const totalFullBaths = (aboveGradeFullBaths + belowGradeFullBaths).toString();

  const explicitZeroAdjustments: Partial<Record<SalesComparisonAdjustmentKey, true>> | undefined =
    manualAdjustments
      ? (() => {
          const isExplicitZero = (value: unknown) =>
            isDefined(value) && schAloftPrice.parse(value).equals(AloftPrice.zero);

          const result: Partial<Record<SalesComparisonAdjustmentKey, true>> = {};

          if (isExplicitZero(manualAdjustments.dateOfSale)) result.saleDateAdjustment = true;
          if (isExplicitZero(manualAdjustments.site)) result.siteSizeAdjustment = true;
          if (isExplicitZero(manualAdjustments.quality)) result.qualityAdjustment = true;
          if (isExplicitZero(manualAdjustments.condition)) result.conditionAdjustment = true;
          if (isExplicitZero(manualAdjustments.gla)) result.glaAdjustment = true;
          if (isExplicitZero(manualAdjustments.bedrooms)) result.bedCountAdjustment = true;
          if (
            isExplicitZero(manualAdjustments.bathAboveGrade) ||
            isExplicitZero(manualAdjustments.bathBelowGrade)
          ) {
            result.bathCountAdjustment = true;
          }
          if (isExplicitZero(manualAdjustments.basement)) result.basementAdjustment = true;
          if (isExplicitZero(manualAdjustments.garageCarport)) result.garageAdjustment = true;
          if (isExplicitZero(manualAdjustments.porchPatioDeck)) result.porchAdjustment = true;
          if (isExplicitZero(manualAdjustments.attic)) result.atticAdjustment = true;
          if (isExplicitZero(manualAdjustments.fireplace)) result.fireplaceAdjustment = true;
          if (isExplicitZero(manualAdjustments.poolSpa)) result.poolAdjustment = true;
          if (isExplicitZero(manualAdjustments.shed)) result.shedAdjustment = true;
          if (isExplicitZero(manualAdjustments.barn)) result.barnAdjustment = true;
          if (isExplicitZero(manualAdjustments.adu)) result.aduAdjustment = true;
          if (isExplicitZero(manualAdjustments.workshop)) result.workshopAdjustment = true;

          return Object.keys(result).length > 0 ? result : undefined;
        })()
      : undefined;

  return {
    imageUrl: propertyImage || '',
    address,
    dataSource:
      typeof improvements?.featureGridDataSources === 'string'
        ? improvements.featureGridDataSources
        : '',
    proximity: distanceToSubject ? `${distanceToSubject} miles` : '',
    salesPrice:
      transaction.status === 'SettledSale' ? formatAdjustmentPrice(transaction.price) : '',
    listingPrice: formatAdjustmentPrice(transaction.originalListPrice),
    dom: (transaction.daysOnMarket ?? '').toString(),
    contractDate: transaction.contractDate
      ? AloftDate.fromIsoString(`${transaction.contractDate}-01`).format('MM/yy')
      : '',
    saleDate: transaction.saleDate
      ? AloftDate.fromIsoString(`${transaction.saleDate}-01`).format('MM/yy')
      : '',
    saleDateAdjustment: adjustments?.dateOfSale?.adjustment?.toString(true) ?? '',
    siteSize: site.area ? AloftArea.schema.parse(site.area).toString(true) : '',
    siteInfluence: site?.influence ?? '',
    view: site?.view ?? '',
    siteSizeAdjustment: adjustments?.site?.size?.adjustment?.toString(true) ?? '',
    quality: `Q${improvements.uadQuality ?? '-'}/${improvements.craftsmanQuality ?? '-'}${getFoundationDisplay(improvements)}`,
    qualityAdjustment: adjustments?.quality?.adjustment?.toString(true) ?? '',
    condition: `C${improvements.uadCondition ?? '-'}/E${improvements.effectiveAge ?? '-'}`,
    conditionAdjustment: adjustments?.condition?.adjustment?.toString(true) ?? '',
    gla: improvements.gla
      ? AloftArea.fromNumber(improvements.gla.unitAmount, improvements.gla.unit).toString(true)
      : '',
    glaAdjustment: adjustments?.mainDwelling?.gla?.adjustment?.toString(true) ?? '',
    bedCount: improvements.bedrooms ? improvements.bedrooms.toString() : '',
    bedCountAdjustment: adjustments?.bedrooms?.adjustment?.toString(true) ?? '',
    bathCount: totalHalfBaths || totalFullBaths ? `${totalFullBaths} / ${totalHalfBaths}` : '',
    bathCountAdjustment: getBathCountAdjustment(adjustments),
    basement: formatFeatureAreas([
      {
        area: improvements.basement?.totalArea?.unitAmount,
        unit: improvements.basement?.totalArea?.unit,
        hasFeature: improvements.basement?.hasFeature,
      },
      {
        area: improvements.basement?.finishedArea?.unitAmount,
        unit: improvements.basement?.finishedArea?.unit,
        hasFeature: improvements.basement?.hasFeature,
      },
    ]),
    basementAdjustment: adjustments?.basement?.adjustment?.toString(true) ?? '',
    garage: getAbbreviatedCarportGarage(improvements) ?? '',
    garageAdjustment: adjustments?.garageCarport?.adjustment?.toString(true) ?? '',
    porch: formatFeatureAreas([
      {
        area: improvements.porch?.area?.unitAmount,
        unit: improvements.porch?.area?.unit,
        type: 'Porch',
        hasFeature: improvements.porch?.hasFeature,
      },
      {
        area: improvements.patio?.area?.unitAmount,
        unit: improvements.patio?.area?.unit,
        type: 'Patio',
        hasFeature: improvements.patio?.hasFeature,
      },
      {
        area: improvements.deck?.area?.unitAmount,
        unit: improvements.deck?.area?.unit,
        type: 'Deck',
        hasFeature: improvements.deck?.hasFeature,
      },
    ]),
    porchAdjustment: adjustments?.porchPatioDeck?.adjustment?.toString(true) ?? '',
    attic: formatFeatureArea({
      area: improvements.attic?.area?.unitAmount,
      unit: improvements.attic?.area?.unit,
      hasFeature: improvements.attic?.hasFeature,
    }),
    atticAdjustment: adjustments?.attic?.adjustment?.toString(true) ?? '',
    fireplace:
      improvements.fireplace?.count && improvements.fireplace?.hasFeature
        ? `${improvements.fireplace.count} ${improvements.fireplace.type ? improvements.fireplace.type : ''}`.trim()
        : '',
    fireplaceAdjustment: adjustments?.fireplace?.adjustment?.toString(true) ?? '',
    pool: formatFeatureAreas([
      {
        area: improvements.pool?.area?.unitAmount,
        unit: improvements.pool?.area?.unit,
        type: 'Pool',
        hasFeature: improvements.pool?.hasFeature,
      },
      {
        area: improvements.spa?.area?.unitAmount,
        unit: improvements.spa?.area?.unit,
        type: 'Spa',
        hasFeature: improvements.spa?.hasFeature,
      },
    ]),
    poolAdjustment: adjustments?.pool?.adjustment?.toString(true) ?? '',
    shed: formatFeatureArea({
      area: improvements.shed?.area?.unitAmount,
      unit: improvements.shed?.area?.unit,
      hasFeature: improvements.shed?.hasFeature,
    }),
    shedAdjustment: adjustments?.shed?.adjustment?.toString(true) ?? '',
    barn: formatFeatureArea({
      area: improvements.barn?.area?.unitAmount,
      unit: improvements.barn?.area?.unit,
      hasFeature: improvements.barn?.hasFeature,
    }),
    adu: formatFeatureArea({
      area: improvements.adu?.area?.unitAmount,
      unit: improvements.adu?.area?.unit,
      hasFeature: improvements.adu?.hasFeature,
    }),
    aduAdjustment: adjustments?.adu?.adjustment?.toString(true) ?? '',
    workshop: formatFeatureArea({
      area: improvements.workshop?.area?.unitAmount,
      unit: improvements.workshop?.area?.unit,
      hasFeature: improvements.workshop?.hasFeature,
    }),
    workshopAdjustment: adjustments?.workshop?.adjustment?.toString(true) ?? '',
    barnAdjustment: adjustments?.barn?.adjustment?.toString(true) ?? '',
    netAdjPrice: adjustments?.netAdjustment?.adjustment?.toString(true) ?? '',
    adjSalesPrice: adjustments?.adjustedSalePrice?.adjustment?.toString(true) ?? '',
    netAdj: adjustments?.netAdjustmentPct.value ?? '',
    grossAdj: adjustments?.grossAdjustmentPct.value ?? '',
    explicitZeroAdjustments,
    compType: id === 'subject' ? undefined : (data as CompPropertyDataState['compData'])?.compType,
    position: compSequence.indexOf(id) + 1,
    arv,
    yearBuilt,
  };
};
